<template>
  <div v-if="contractor" class="details-wrapper">
    <div class="details-header">
      <div>Contractor #{{ contractor.reference_number }}</div>
    </div>
    <CreatedComment :comment="contractor.created_comment" />
    <div v-if="contractor.reference_number" class="details-body">
      <div class="content-panel-wrapper">
        <div v-scroll-spy data-scroll-spy-id="main-tab" class="content-panel">
          <section class="details-section">
            <ContractorDetailsForm :contractor="contractor" @form-data-change="onFormDataChange" />
          </section>
          <section class="details-section">
            <h1>{{ this.$t('contractor.labels.assignments') }}</h1>
            <router-link
              class="router-link-remove-style"
              :to="{
                path: '/assignments/create?contractor_refNum=' + contractor.reference_number
              }"
            >
              <div class="add">
                <span class="remove-underline">New Assignment</span>
              </div>
            </router-link>

            <tol-table
              :table-data="contractorAssignments"
              :columns="assignmentColumns"
              :show-spinner="isAssignmentsLoading"
              :is-row-clickable="true"
              class="assignment"
              @row-click="onRowClick($event)"
            >
              <template v-slot:tableHeader>
                <th-sortable
                  :direction="
                    currentSortFieldForAssignments === 'payroll_num'
                      ? currentSortDirectionForAssignments
                      : 'none'
                  "
                  field-name="payroll_num"
                  @change="onAssignmentSortOrderChange"
                  >{{ $t('contractor.labels.payrollNumber') }}</th-sortable
                >
                <th-sortable
                  :direction="
                    currentSortFieldForAssignments === 'contractor'
                      ? currentSortDirectionForAssignments
                      : 'none'
                  "
                  field-name="contractor"
                  @change="onAssignmentSortOrderChange"
                  >{{ $t('contractor.labels.contractor') }}</th-sortable
                >

                <th-sortable
                  :direction="
                    currentSortFieldForAssignments === 'client'
                      ? currentSortDirectionForAssignments
                      : 'none'
                  "
                  field-name="client"
                  @change="onAssignmentSortOrderChange"
                  >{{ $t('contractor.labels.client') }}</th-sortable
                >

                <th>{{ $t('contractor.labels.approvers') }}</th>
                <th-sortable
                  :direction="
                    currentSortFieldForAssignments === 'startDate'
                      ? currentSortDirectionForAssignments
                      : 'none'
                  "
                  field-name="startDate"
                  @change="onAssignmentSortOrderChange"
                  >{{ $t('contractor.labels.startDate') }}</th-sortable
                >
                <th-sortable
                  :direction="
                    currentSortFieldForAssignments === 'stopDate'
                      ? currentSortDirectionForAssignments
                      : 'none'
                  "
                  field-name="stopDate"
                  @change="onAssignmentSortOrderChange"
                  >{{ $t('contractor.labels.stopDate') }}</th-sortable
                >
                <th-sortable
                  :direction="
                    currentSortFieldForAssignments === 'branch'
                      ? currentSortDirectionForAssignments
                      : 'none'
                  "
                  field-name="branch"
                  @change="onAssignmentSortOrderChange"
                  >{{ $t('contractor.labels.branch') }}</th-sortable
                >
                <th-sortable
                  :direction="
                    currentSortFieldForAssignments === 'status'
                      ? currentSortDirectionForAssignments
                      : 'none'
                  "
                  field-name="status"
                  @change="onAssignmentSortOrderChange"
                  >{{ $t('contractor.labels.status') }}</th-sortable
                >
              </template>
              <template v-slot:status="{ value }">
                <Status :status="value.status" object-type="assignment" />
              </template>
            </tol-table>
          </section>
          <section class="details-section">
            <h1>{{ $t('contractor.labels.attachments') }}</h1>
            <div v-if="canViewAttachment && contractor && contractor.reference_number">
              <Attachment
                :reference-number="contractor.reference_number"
                owner="CONTRACTOR"
                @attachment-success="refreshAttachments"
              />
              <AttachmentsList
                attachment-type="CONTRACTOR"
                :attachments="contractorAttachments"
                :attachment-options="attachmentOptions"
                @refresh="refreshAttachments"
              />
            </div>
          </section>

          <section class="details-section">
            <h1>{{ $t('contractor.labels.history') }}</h1>
            <tol-table
              :table-data="contractorHistories"
              :columns="historiesColumns"
              :expandable="true"
              class="ctrhistory"
            >
              <template v-slot:tableHeader>
                <th></th>
                <th>{{ $t('contractor.history.listing.labels.created_at') }}</th>
                <th>{{ $t('contractor.history.listing.labels.created_by_name') }}</th>
                <th>{{ $t('contractor.history.listing.labels.payroll') }}</th>
                <th>{{ $t('contractor.history.listing.labels.name') }}</th>
                <th class="ctremail">{{ $t('contractor.history.listing.labels.email') }}</th>
                <th>{{ $t('contractor.history.listing.labels.entity') }}</th>
                <th>{{ $t('contractor.history.listing.labels.pay_to_name') }}</th>
                <th>{{ $t('contractor.history.listing.labels.comment') }}</th>
              </template>

              <template v-slot:created_at="{ value }">
                {{ formatDate(value.created_at) }}
              </template>

              <template v-slot:expanded="{ value }">
                <ContractorDetailsForm
                  :contractor="getSelectedHistoryRecord(value.id)"
                  :is-history-record="true"
                />
              </template>
            </tol-table>
          </section>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { AppActions } from '@/app/app.keys';
import { ContractorDetailsActions, ContractorDetailsGetters } from './contractor-details.keys';
import ContractorDetailsForm from './components/ContractorDetailsForm';
import TolTable from '@/app/components/TolTable';
import CreatedComment from '@/app/components/CreatedComment';
import ThSortable from '@/app/components/ThSortable';
import Status from '@/app/components/Status';
import AttachmentsList from '@/app/components/attachments/AttachmentsList';
import Attachment from '@/app/components/attachments/Attachment';
import format from 'date-fns/format';
export default {
  name: 'ContractorDetails',
  components: {
    ContractorDetailsForm,
    TolTable,
    ThSortable,
    CreatedComment,
    AttachmentsList,
    Attachment,
    Status
  },
  data() {
    return {
      sidebarMenu: [
        {
          label: this.$t('contractor.labels.personalDetails'),
          submenu: [
            { label: this.$t('contractor.labels.governmentNumbers') },
            { label: this.$t('contractor.labels.supportingDocuments') },
            { label: this.$t('contractor.labels.miscDetails') }
          ]
        },
        { label: this.$t('contractor.labels.assignments') },
        { label: this.$t('contractor.labels.attachments') },
        { label: this.$t('contractor.labels.history') }
      ],
      assignmentColumns: [
        'payroll_num',
        'contractor',
        'client',
        'approvers',
        'startDate',
        'stopDate',
        'branch',
        'status'
      ],
      historiesColumns: [
        'created_at',
        'created_by_name',
        'payroll_num',
        'name',
        'email',
        'entity',
        'pay_to_name',
        'created_comment'
      ],
      attachmentOptions: [
        'Articles of Incorporation or Incorporation Papers',
        'Master Business Licence',
        'Banking Information',
        'HST Form',
        'GST Form',
        'QST Form',
        'H & S Booklet SignOff',
        'BackCheck Report',
        'BackCheck Consent Form',
        'Signed Attestation and Permission to Share Info',
        'Documentation of Vaccination/Exemption',
        'AODA Form',
        'Direct Deposit Form',
        'Work Permit',
        'Study Permit',
        'Emergency Contacts',
        'Consent for Online Payslips',
        'Background screening cost Acknowledgement',
        'BGC Release Authorization',
        'Staff Contract',
        'WHMIS',
        'Privacy Policy',
        'Online Health & Safety Training',
        'Change of Inc. Letter',
        'Documentation of Vaccination/Exemption',
        'Signed Attestation and Permission to Share Info',
        'Consent For Audit Purpose',
        'Other'
      ]
    };
  },
  computed: {
    isAssignmentsLoading() {
      return this.$store.getters[ContractorDetailsGetters.IsAssignmentsLoading];
    },
    currentUser() {
      return this.$store.state.currentUser;
    },
    contractor: {
      get() {
        return this.$store.getters[ContractorDetailsGetters.CurrentContractor];
      }
    },
    contractorAssignments: {
      get() {
        return this.$store.getters[ContractorDetailsGetters.CurrentContractorAssignments];
      }
    },
    currentSortDirectionForAssignments() {
      return this.$store.getters[ContractorDetailsGetters.ContractorAssignmentsSortOrder]
        .sortDirection;
    },

    currentSortFieldForAssignments() {
      return this.$store.getters[ContractorDetailsGetters.ContractorAssignmentsSortOrder].sortBy;
    },
    contractorHistories: {
      get() {
        return this.$store.getters[ContractorDetailsGetters.CurrentContractorHistories];
      }
    },
    contractorAttachments() {
      return this.$store.getters[ContractorDetailsGetters.CurrentContractorAttachments];
    },
    canViewAttachment() {
      return this.currentUser.permissions['CONTRACTOR']['VIEW_ATTACHMENT'];
    }
  },

  destroyed() {
    this.$store.dispatch(ContractorDetailsActions.ResetCurrentContractor);
  },
  mounted() {
    this.$store.dispatch(AppActions.SetPageTitle, this.$t('contractor.details.title'));
    this.$store.dispatch(
      ContractorDetailsActions.RetrieveContractor,
      this.$route.params.reference_number
    );
  },
  methods: {
    onRowClick(evt) {
      this.$router.push(`/assignments/${evt.referenceNumber}`);
    },
    onAssignmentSortOrderChange(sortOrder) {
      this.$store.dispatch(ContractorDetailsActions.SetContractorAssignmentsSortOrder, sortOrder);
    },
    getSelectedHistoryRecord(contractorId) {
      return this.$store.getters[ContractorDetailsGetters.SelectedHistoryRecord](contractorId);
    },
    formatDate(d) {
      return d ? format(d, 'YYYY-MM-DD') : null;
    },
    onFormDataChange() {
      this.isOnScreenDataModified = true;
    },
    refreshAttachments() {
      this.$store.dispatch(
        ContractorDetailsActions.RetrieveContractorAttachments,
        this.$route.params.reference_number
      );
    }
  }
};
</script>

<style scoped lang="scss">
@import '@/app/styles/colors.scss';
@import '@/app/styles/variables.scss';

.router-link-remove-style {
  text-decoration: none;
  justify-content: flex-end;
  display: flex;
  margin-bottom: 10px;
}
.add {
  width: 200px;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 45px;
  font-weight: bold;
  background-color: $secondary-green;
  color: $white;
}
.right-side {
  float: right;
  margin-left: 15px;
}
.contractors-new-assignment {
  position: relative;
  top: 20px;
  left: 50vw;
}

.details-wrapper {
  display: flex;
  flex-direction: column;

  background-color: $background-level-2;
  .details-header {
    display: flex;

    padding: $details-padding;
    border-bottom: 2px solid $row-border;
    justify-content: space-between;
  }

  .details-body {
    position: relative;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    border-right: 4px solid rgba(255, 255, 255, 0);
    &.modified {
      border-right: 4px solid $modified-form;
    }

    .content-panel-wrapper {
      flex: 0 85%;
      margin: 0 auto;
      .content-panel {
        display: block;
        background-color: $white;
        padding: 60px;
      }
    }

    .details-section {
      padding-bottom: 25px;

      h1 {
        border-bottom: 1px solid $row-border;
      }
      .add {
      }
    }
  }
}
</style>
